import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BodyText, CheckboxTile, CheckboxGroup, Flex } from "@utilitywarehouse/hearth-react";

import WhyWeWant from "../../WhyWeWant";
import { QuestionsDataTest } from "../constants";
import NavigationButtons from "../NavigationButtons";

export interface MultipleChoiceQuestionProps {
  step: string;
  title: string;
  subTitle?: string;
  whyWeWantTexts?: string[];
  onContinue: () => void;
  options: Record<string, string>;
  values?: Record<string, boolean>;
  onChange: (values: Record<string, boolean>) => void;
  requireAnswer?: boolean;
}

const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps> = ({
  step,
  title,
  subTitle,
  whyWeWantTexts,
  onContinue,
  options,
  values,
  onChange,
  requireAnswer,
}) => {
  const navigate = useNavigate();

  const [hasError, setHasError] = useState(false);

  const handleNoneOfTheAbove = (value: string) => { 
    switch (value) {
      case "noneOfTheAbove":
        return { 
          pregnant: false, 
          childrenUnder2: false, 
          childrenBetween2And4: false, 
          childrenBetween5And15: false, 
          psr: undefined, 
        }; 
      case "pregnant":
      case "childrenUnder2":
      case "childrenBetween2And4":
      case "childrenBetween5And15":
        return { 
          noneOfTheAbove: false,
          psr: undefined,
        };
      default:
        return null; 
    }
  }; 

  const onCheckboxChange = (value: string, checked: boolean) => {
    const newValues = {
      ...values,
      [value]: checked,
      ...(handleNoneOfTheAbove(value))
    };

    onChange(newValues);
  };

  const onContinueClick = () => {
    if (!requireAnswer) {
      setHasError(false);
      onContinue();
      return;
    }

    if (Object.keys(options).some(( value ) => values?.[value])) {
      setHasError(false);
      onContinue();
    } else {
      setHasError(true);
    }
  };

  // We need to remap our internal state for vulnerabilities that looks like { key1: true, key2: false } to ["key1"], because the hearth api for checkboxes uses `values` as an array of strings (to be in the array only if "true")
  const mappedVaules = values ? Object.entries(values).filter(([, value]) => !!value).map(([key]) => key) : [];

  return (
    <Flex direction="column" gap="500">
      {step ? (
        <BodyText weight="semibold">
        {step}
        </BodyText>
      ) : null}

      <Flex direction="column" gap="150">
        <BodyText weight="semibold" data-testid={QuestionsDataTest.TITLE}>
          {title}

          {subTitle ? (
            <BodyText color="secondary" data-testid={QuestionsDataTest.SUB_TITLE}>
              {subTitle}
            </BodyText>
          ) : null}
        </BodyText>

        <CheckboxGroup
          contentWidth="100%"
          value={mappedVaules}
          validationStatus={hasError ? "invalid" : undefined}
          validationPlacement="bottom"
          validationText={
            <BodyText
              data-testid={QuestionsDataTest.ERROR}
            >
              Please select a response to continue
            </BodyText>
          }
        >
          {
            Object.entries(options).map(([value, title]) => (
              <CheckboxTile
                key={value}
                label={title}
                value={value}
                checked={Boolean(values?.[value])}
                data-testid={QuestionsDataTest.CHECKBOX_OPTIONS}
                onCheckedChange={(checked) => onCheckboxChange(value, checked)}
              />
            ))
          }
        </CheckboxGroup>
      </Flex>

      {whyWeWantTexts ? (
        <WhyWeWant whyWeWantTexts={whyWeWantTexts} />
      ) : null}

      <NavigationButtons
        onBack={() => navigate(-1)}
        onContinue={onContinueClick}
      />
    </Flex>
  );
};

export default MultipleChoiceQuestion;